import React, { useState, useEffect } from 'react';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import { Navigate } from 'react-router-dom';

const TABS = ['Profile', 'Orders', 'Favorites'];

const UserProfilePage = () => {
  const { profile, updateProfile, removeFromFavorites } = useProfile();
  const { user, logout } = useAuth();

  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || ''
  });
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('Profile');

  useEffect(() => {
    setForm({
      name: user?.name || '',
      phone: user?.phone || '',
      address: user?.address || ''
    });
    setAvatar(user?.avatar || '');
  }, [user]);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = e => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      updateProfile({ ...form, avatar });
      setLoading(false);
      setEditMode(false);
      toast.success('Profile updated!');
    }, 1000);
  };

  const handleAvatarChange = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) return <Loader text="Saving changes..." />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 60%, #fb923c 100%)',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Banner */}
      <div
        style={{
          width: '100%',
          height: 240, // slightly taller for more space
          background: 'linear-gradient(90deg, #008751 60%, #fb923c 100%)',
          position: 'relative'
        }}
      >
        <img
          src="/images/chef/Background img.jpg"
          alt="Banner"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.18,
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: 1
          }}
        />
        {/* Avatar with edit icon */}
        <div
          className="profile-avatar-wrapper"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -80, // slightly more negative for desktop
            transform: 'translateX(-50%)',
            textAlign: 'center',
            zIndex: 2
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
              src={avatar || '/default-avatar.png'}
              alt="Avatar"
              className="profile-avatar-img"
              style={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '6px solid #fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.13)'
              }}
            />
            {editMode && (
              <label style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                background: '#008751',
                borderRadius: '50%',
                padding: 10,
                cursor: 'pointer',
                border: '2px solid #fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <span role="img" aria-label="edit" style={{ color: '#fff', fontSize: 22 }}>📷</span>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Profile Card */}
      <div className="profile-card" style={{
        maxWidth: 950,
        width: '100%', // changed from '97%'
        margin: '140px auto 0 auto',
        background: 'white',
        borderRadius: 28,
        boxShadow: '0 8px 32px rgba(0,0,0,0.13)',
        padding: '2.5rem 1rem 1.5rem 1rem', // less padding for mobile
        position: 'relative',
        boxSizing: 'border-box'
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          marginBottom: '2.5rem'
        }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: activeTab === tab ? 'linear-gradient(90deg, #008751 60%, #fb923c 100%)' : '#f3f4f6',
                color: activeTab === tab ? '#fff' : '#008751',
                border: 'none',
                borderRadius: 16,
                padding: '0.8rem 2.2rem',
                fontWeight: 800,
                fontSize: '1.1rem',
                cursor: 'pointer',
                boxShadow: activeTab === tab ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'Profile' && (
          <>
            {/* Account Stats */}
            <div style={{
              display: 'flex',
              gap: '2rem',
              justifyContent: 'center',
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                background: '#f8f9fa',
                borderRadius: 14,
                padding: '1.2rem 2rem',
                width: '100%',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}>
                <div style={{ fontSize: 32, color: '#008751', fontWeight: 900 }}>
                  <span role="img" aria-label="orders">🛒</span> {profile?.totalOrders ?? 0}
                </div>
                <div style={{ color: '#6b7280', fontWeight: 700 }}>Orders</div>
              </div>
              <div style={{
                background: '#f8f9fa',
                borderRadius: 14,
                padding: '1.2rem 2rem',
                width: '100%',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}>
                <div style={{ fontSize: 32, color: '#fb923c', fontWeight: 900 }}>
                  <span role="img" aria-label="favorites">❤️</span> {(profile?.favoriteItems || []).length}
                </div>
                <div style={{ color: '#6b7280', fontWeight: 700 }}>Favorites</div>
              </div>
              <div style={{
                background: '#f8f9fa',
                borderRadius: 14,
                padding: '1.2rem 2rem',
                width: '100%',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}>
                <div style={{ fontSize: 22, color: '#008751', fontWeight: 700 }}>
                  <span role="img" aria-label="calendar">📅</span> {profile?.joinDate || 'N/A'}
                </div>
                <div style={{ color: '#6b7280', fontWeight: 700 }}>Joined</div>
              </div>
            </div>

            {/* Profile Info and Edit */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h1 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#008751', margin: 0 }}>
                {user?.name}
              </h1>
              <div style={{ color: '#6b7280', fontSize: '1.1rem', margin: '0.5rem 0' }}>
                <b>Email:</b> {user?.email}
              </div>
              <div style={{ color: '#6b7280', fontSize: '1.1rem' }}>
                <b>Phone:</b> {user?.phone}
              </div>
              <div style={{ color: '#6b7280', fontSize: '1.1rem' }}>
                <b>Address:</b> {user?.address}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button
                  onClick={() => setEditMode(!editMode)}
                  style={{
                    background: editMode ? '#fb923c' : '#008751',
                    color: 'white',
                    border: 'none',
                    padding: '0.7rem 1.5rem',
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    marginRight: '1rem'
                  }}
                >
                  {editMode ? 'Cancel' : 'Edit Profile'}
                </button>
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  style={{
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '0.7rem 1.5rem',
                    borderRadius: 12,
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Edit Profile Form */}
            {editMode && (
              <form onSubmit={handleSave} style={{ marginBottom: '2rem', textAlign: 'center' }}>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    required
                    style={{
                      flex: 1,
                      padding: '1rem',
                      border: '2px solid #e9ecef',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      width: '100%'
                    }}
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    required
                    style={{
                      flex: 1,
                      padding: '1rem',
                      border: '2px solid #e9ecef',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      width: '100%'
                    }}
                  />
                  <input
                    type="text"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Delivery Address"
                    required
                    style={{
                      flex: 2,
                      padding: '1rem',
                      border: '2px solid #e9ecef',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      width: '100%'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    marginTop: '1.5rem',
                    background: 'linear-gradient(135deg, #008751, #fb923c)',
                    color: 'white',
                    border: 'none',
                    padding: '1rem 2.5rem',
                    borderRadius: 14,
                    fontWeight: 900,
                    fontSize: '1.1rem',
                    cursor: 'pointer'
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}
          </>
        )}

        {activeTab === 'Orders' && (
          <div style={{
            marginBottom: '2rem',
            background: '#f8f9fa',
            borderRadius: 14,
            padding: '1.5rem'
          }}>
            <h2 style={{
              fontSize: '1.2rem',
              fontWeight: '800',
              color: '#008751',
              marginBottom: '1rem'
            }}>
              🧾 Recent Orders
            </h2>
            {(profile?.orderHistory || []).length === 0 ? (
              <div style={{ color: '#9ca3af', textAlign: 'center' }}>
                No orders yet.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {(profile.orderHistory || []).slice(-8).reverse().map((order, idx) => (
                  <li key={order.id || idx} style={{
                    background: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    padding: '1rem',
                    marginBottom: '0.5rem',
                    transition: 'box-shadow 0.2s',
                    cursor: 'pointer'
                  }}>
                    <div style={{ fontWeight: '700', color: '#008751', fontSize: '1.1rem' }}>
                      Order #{order.id?.toString().slice(-5) || idx + 1}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                      {order.date || 'N/A'}
                    </div>
                    <div style={{ color: '#fb923c', fontWeight: '700', fontSize: '1.05rem' }}>
                      Total: ₦{order.total?.toLocaleString() || 'N/A'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'Favorites' && (
          <div style={{
            background: '#f8f9fa',
            borderRadius: 14,
            padding: '1.5rem'
          }}>
            <h2 style={{
              fontSize: '1.2rem',
              fontWeight: '800',
              color: '#008751',
              marginBottom: '1rem'
            }}>
              ❤️ Favorites
            </h2>
            {(profile?.favoriteItems || []).length === 0 ? (
              <div>No favorite dishes yet.</div>
            ) : (
              <ul style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', listStyle: 'none', padding: 0 }}>
                {(profile.favoriteItems || []).map((item, idx) => (
                  <li key={item.id || idx} style={{
                    background: 'white',
                    borderRadius: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    padding: '0.7rem 1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.7rem',
                    transition: 'box-shadow 0.2s'
                  }}>
                    <span>{item.name}</span>
                    <button
                      onClick={() => removeFromFavorites(item.id)}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '7px',
                        padding: '0.2rem 0.7rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutConfirm && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '18px',
              padding: '2rem',
              minWidth: '320px',
              maxWidth: '95vw',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              position: 'relative',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#dc2626', fontWeight: '900', marginBottom: '1rem' }}>
                Confirm Logout
              </h3>
              <p>Are you sure you want to log out?</p>
              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  style={{
                    background: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '0.7rem 1.5rem',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={logout}
                  style={{
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '0.7rem 1.5rem',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      <style>{`
        html, body {
          width: 100%;
          overflow-x: hidden !important;
          margin: 0;
          padding: 0;
        }
        .profile-card {
          box-sizing: border-box;
          max-width: 950px;
          width: 100%;
        }
        @media (max-width: 900px) {
          .profile-card { padding: 1.2rem !important; }
        }
        @media (max-width: 600px) {
          .profile-card { 
            padding: 0.5rem !important; 
            margin: 60px auto 0 auto !important;
            max-width: 100% !important;
            width: 100% !important;
            border-radius: 0 !important;
          }
          .profile-card h1 { font-size: 1.3rem !important; }
          .profile-card input,
          .profile-card button {
            font-size: 0.95rem !important;
            min-width: 0 !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .profile-avatar-wrapper {
            bottom: -35px !important;
          }
          .profile-avatar-img {
            width: 80px !important;
            height: 80px !important;
            border-width: 3px !important;
          }
          .profile-card ul,
          .profile-card .orders-list {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 0.7rem !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .profile-card .profile-stats,
          .profile-card > div[style*="display: flex"] {
            flex-direction: column !important;
            gap: 0.7rem !important;
            width: 100% !important;
          }
          .profile-card > div,
          .profile-card > form,
          .profile-card > ul {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .profile-card li {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .profile-card .profile-stats > div {
            min-width: 0 !important;
            width: 100% !important;
            box-sizing: border-box !important;
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }
        }
        @media (max-width: 400px) {
          .profile-avatar-img {
            width: 60px !important;
            height: 60px !important;
          }
          .profile-avatar-wrapper {
            bottom: -20px !important;
          }
          .profile-card { padding: 0.2rem !important; }
          .profile-card h1 { font-size: 1.1rem !important; }
        }
      `}</style>
    </div>
  );
};

export default UserProfilePage;